// Geçmiş Rapor Aktarımı — kopya sabitleri + saf UI yardımcıları (Task 12).
// Bu dosya Task 13 (parti ekranı /projects/import), Task 14 (ProjectDetail özet +
// "Rapordan doldur" modalı) ve Task 15 (FormFill köken/provenance) tarafından tüketilir.
// Component'ler burada YOK — yalnız render eder; mantık burada kilitlenir + testlidir
// (bkz. report-import-ui.test.ts). Kopya kaynak sırası: body < Eng review < Tasarım
// review < Regresyon analizi (sonraki katman öncekini override eder) — task-12-brief.md.

import {
  COMPANY_NAME_SIMILAR_MIN,
  SECTOR_LABELS,
  REPORT_IMPORT_ALLOWED_MIMES,
  REPORT_IMPORT_MAX_FILE_BYTES,
  REPORT_TYPE_LABELS,
  coerceReportType,
  companyNameSimilarity,
  isFormValueFilled,
  isSectorBlockCode,
  normalizeCompanyName,
  normalizeSectorCode,
  stripSectorPrefix,
  type ImportBatchRow,
  type ImportProgress,
  type ImportStats,
  type ImportSuggestion,
  type ImportSummary,
  type PeriodType,
  type ProjectProvenanceResponse,
  type ReportImport,
  type ReportImportFile,
  type ReportImportStatus,
  type ReportType,
  type ResponseProvenance,
  type SectorCode,
} from "@mahir/shared";
import { ApiError, uploadFileWithProgress } from "../api/client";
import { presignImportFile } from "../api/report-import";
import { fillTemplate } from "./period-ui";
import { IMPORT_COPY } from "./report-import-copy";

// Ayrı dosyaya taşındı (600 satır sınırı) — tüketiciler yine buradan alır.
export { IMPORT_COPY, type ImportCopyKey } from "./report-import-copy";

// ---------------------------------------------------------------------------
// Durum etiketleri / ton
// ---------------------------------------------------------------------------
export const STATUS_LABELS: Record<ReportImportStatus, string> = {
  uploaded: "Yüklendi",
  queued: "Sırada",
  mapping: "Eşleştiriliyor",
  extracting: "Çıkarılıyor",
  writing: "Kaydediliyor",
  done: "Tamam",
  // Eng review (2026-09-24) — Geri çek akışı.
  archived: "Geri çekildi",
  failed: "Başarısız",
  cancelled: "İptal edildi",
};

export type StatusTone = "neutral" | "progress" | "success" | "error";

export function statusTone(status: ReportImportStatus): StatusTone {
  switch (status) {
    case "uploaded":
    case "queued":
    case "archived":
    case "cancelled":
      return "neutral";
    case "mapping":
    case "extracting":
    case "writing":
      return "progress";
    case "done":
      return "success";
    case "failed":
      return "error";
  }
}

export function progressPercent(progress: ImportProgress | null): number {
  if (!progress || progress.sectionsTotal === 0) return 0;
  return Math.round((progress.sectionsDone / progress.sectionsTotal) * 100);
}

/**
 * 5A — durum hücresindeki aşama metni. Not: backend enum'unda ayrı bir "reading"
 * durumu yok ("mapping" tek durum); `IMPORT_COPY.stageReading` T13'ün ince taneli
 * alt-aşama göstergesi için ayrılmıştır (bkz. task-12-report.md "deviations").
 * Burada `status` doğrudan kendi ismiyle eşlenir — en az şaşırtan varsayılan.
 */
export function stageLabel(status: ReportImportStatus, progress: ImportProgress | null): string {
  switch (status) {
    case "uploaded":
    case "queued":
      return IMPORT_COPY.stageQueued;
    case "mapping":
      return IMPORT_COPY.stageMapping;
    case "extracting":
      // progress henüz yazılmamışsa (aşamaya yeni girildi) ya da bölüm sayısı bilinmiyorsa
      // ham "{done}/{total}" şablonu ekrana sızmasın (manuel test 2026-09-30) — sayısız metin.
      return progress && progress.sectionsTotal > 0
        ? fillTemplate(IMPORT_COPY.stageExtracting, {
            done: progress.sectionsDone,
            total: progress.sectionsTotal,
          })
        : IMPORT_COPY.stageExtractingNoCount;
    case "writing":
      return IMPORT_COPY.stageWriting;
    case "done":
      return IMPORT_COPY.stageDone;
    case "archived":
    case "failed":
    case "cancelled":
      return STATUS_LABELS[status];
  }
}

// ---------------------------------------------------------------------------
// Dosya doğrulama / gruplama
// ---------------------------------------------------------------------------
export type RejectReason = "indd" | "doc" | "size" | "mime";

const ALLOWED_MIME_SET: readonly string[] = REPORT_IMPORT_ALLOWED_MIMES;

function extOf(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx === -1 ? "" : fileName.slice(idx + 1).toLowerCase();
}

function stemOf(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx === -1 ? fileName : fileName.slice(0, idx);
}

/** Dosya kabul/red kararı — sıra: InDesign → eski .doc → boyut → mime/uzantı. */
export function rejectImportFile(file: File): RejectReason | null {
  const ext = extOf(file.name);
  if (ext === "indd" || ext === "idml") return "indd";
  if (ext === "doc") return "doc";
  if (file.size > REPORT_IMPORT_MAX_FILE_BYTES) return "size";
  if (ext !== "pdf" && ext !== "docx") return "mime";
  // Tarayıcı bazen boş `type` döner — uzantı zaten pdf/docx ise mime type'a
  // yalnızca DOLUYSA ve tanınmayan bir değerse red uygularız.
  if (file.type && !ALLOWED_MIME_SET.includes(file.type)) return "mime";
  return null;
}

export interface FileGroupRejected {
  file: File;
  reason: RejectReason;
}

export interface FileGroup {
  stem: string;
  pdf?: File;
  docx?: File;
  rejected?: FileGroupRejected;
}

/** Aynı gövdeli (uzantısız ad) PDF + DOCX'i tek satıra gruplar (K16 — Word varsa birincil). */
export function groupFilesByStem(files: File[]): FileGroup[] {
  const order: string[] = [];
  const map = new Map<string, FileGroup>();
  for (const file of files) {
    const stem = stemOf(file.name);
    let group = map.get(stem);
    if (!group) {
      group = { stem };
      map.set(stem, group);
      order.push(stem);
    }
    const reason = rejectImportFile(file);
    if (reason) {
      group.rejected ??= { file, reason };
      continue;
    }
    const ext = extOf(file.name);
    if (ext === "pdf") group.pdf = file;
    else if (ext === "docx") group.docx = file;
  }
  return order.map((stem) => map.get(stem)!);
}

// ---------------------------------------------------------------------------
// S6 — TÜRKÇE İYELİK EKİ (parti e290 §11, UI/kopya)
// ---------------------------------------------------------------------------
//
// `bandImported` kopyasında ek SABİTTİ (`{low}'u düşük güven`): 3 → "3'u" (doğrusu
// "3'ü"), 7 → "7'u" (doğrusu "7'si"). Dört raporun ÜÇÜNDE yanlış görünüyordu.
// Ek, sayının Türkçe okunuşunun SON SÖZCÜĞÜNDEN türer; sözcük ünlüyle bitiyorsa
// araya kaynaştırma "s"si girer ve ünlü, son ünlünün kalınlık/yuvarlaklığına uyar.

/** Son hanenin sözcüğü (sıfır değilse) — 1..9. */
const DIGIT_WORDS = ["", "bir", "iki", "üç", "dört", "beş", "altı", "yedi", "sekiz", "dokuz"];
/** Onlar basamağının sözcüğü — 10..90. */
const TENS_WORDS = [
  "",
  "on",
  "yirmi",
  "otuz",
  "kırk",
  "elli",
  "altmış",
  "yetmiş",
  "seksen",
  "doksan",
];
/** Büyüklük sözcükleri — yüz/bin/milyon/milyar. */
const MAGNITUDE_WORDS: readonly { value: number; word: string }[] = [
  { value: 1_000_000_000, word: "milyar" },
  { value: 1_000_000, word: "milyon" },
  { value: 1000, word: "bin" },
  { value: 100, word: "yüz" },
];

/** Sayının Türkçe okunuşunun SON sözcüğü ("24" → "dört", "20" → "yirmi", "100" → "yüz"). */
export function lastNumberWordTr(n: number): string {
  const abs = Math.abs(Math.trunc(n));
  if (abs === 0) return "sıfır";
  const ones = abs % 10;
  if (ones > 0) return DIGIT_WORDS[ones]!;
  const tens = Math.floor(abs / 10) % 10;
  if (tens > 0) return TENS_WORDS[tens]!;
  for (const m of MAGNITUDE_WORDS) {
    if (abs % m.value === 0) return m.word;
  }
  return DIGIT_WORDS[ones] ?? "sıfır";
}

const BACK_UNROUNDED = "aı";
const FRONT_UNROUNDED = "ei";
const BACK_ROUNDED = "ou";

/** Sözcüğün son ünlüsüne göre 3. tekil iyelik ünlüsü (ı/i/u/ü). */
function possessiveVowel(word: string): string {
  for (let i = word.length - 1; i >= 0; i -= 1) {
    const ch = word[i]!;
    if (BACK_UNROUNDED.includes(ch)) return "ı";
    if (FRONT_UNROUNDED.includes(ch)) return "i";
    if (BACK_ROUNDED.includes(ch)) return "u";
    if ("öü".includes(ch)) return "ü";
  }
  return "i";
}

/** Sözcük ünlüyle mi bitiyor (kaynaştırma "s" gerekir)? */
function endsWithVowel(word: string): boolean {
  const last = word.at(-1) ?? "";
  return "aeıioöuü".includes(last);
}

/**
 * S6 — sayıya eklenecek 3. tekil iyelik eki, kesme işaretiyle birlikte:
 * 3 → "'ü", 7 → "'si", 10 → "'u", 4 → "'ü", 6 → "'sı", 100 → "'ü".
 * UI'da satır içi ek YAZILMAZ; kopya şablonu bu fonksiyondan beslenir.
 */
export function possessiveSuffixTr(n: number): string {
  const word = lastNumberWordTr(n);
  const vowel = possessiveVowel(word);
  return endsWithVowel(word) ? `'s${vowel}` : `'${vowel}`;
}

// ---------------------------------------------------------------------------
// Reddedilen köken satırının metni (inceleme, Important)
// ---------------------------------------------------------------------------
//
// S4 mükerrer metin reddi `rejectedReason: "grounding"` ile düşüyor; sabit
// "kaynak doğrulanamadı" / "metin raporda bulunamadı" metinleri YANLIŞ bilgi veriyordu
// (metin raporda birebir var, yalnız başka bir soruya yazılmıştı). Köken `detail`
// alanı doluysa sebep ODUR. Karar saf fonksiyonda tutulur ki iki bileşen ayrışmasın.

/**
 * Köken ayrıntısının satırda/popover'da gösterilecek en uzun hali. `detail` kolonu
 * 400 karaktere kadar (shared şema) — satır içine sığmayan metin üç nokta ile kısalır.
 */
export const PROVENANCE_DETAIL_MAX_SHOWN = 160;

function shortDetail(detail: string): string {
  return detail.length <= PROVENANCE_DETAIL_MAX_SHOWN
    ? detail
    : `${detail.slice(0, PROVENANCE_DETAIL_MAX_SHOWN - 1).trimEnd()}…`;
}

/** Reddedilen satırın başlık metni — ayrıntı varsa onu yazar (kısaltılmış). */
export function rejectedLineText(detail: string | null | undefined): string {
  return detail !== null && detail !== undefined && detail.length > 0
    ? fillTemplate(IMPORT_COPY.rejectedLabelWithDetail, { detail: shortDetail(detail) })
    : IMPORT_COPY.rejectedLabel;
}

/** "Ne önerilmişti?" popover'ındaki sebep — ayrıntı genel sebep metnini EZER. */
export function rejectedReasonText(entry: {
  detail?: string | null;
  rejectedReason?: "grounding" | "schema" | null;
}): string {
  if (entry.detail !== null && entry.detail !== undefined && entry.detail.length > 0) {
    return shortDetail(entry.detail);
  }
  return entry.rejectedReason === "schema"
    ? IMPORT_COPY.rejectedReasonSchema
    : IMPORT_COPY.rejectedReasonGrounding;
}

/**
 * P15 inceleme (karar 2) — köken rozetinde/satırında gösterilecek ölçek eki.
 * Çarpan TR binlik ayırıcısıyla yazılır ("×1.000" / "×1.000.000"); köken satırında
 * çarpan yoksa (eski satır) ek yazılmaz.
 *
 * G2 (parti e290 §6.2) — çarpan 1'den KÜÇÜK olabilir (ölçek ailesi dönüşümü: kaynak
 * Bin TL, sütun Milyon TL → 0,001). O durumda ek "÷1.000 ölçeklendi" olur; çarpan
 * 1'e eşitse (dönüşüm yok) ek yazılmaz.
 */
export function scaledSuffixText(factor: number | null | undefined): string | null {
  if (factor === null || factor === undefined || !Number.isFinite(factor) || factor <= 0) {
    return null;
  }
  if (factor === 1) return null;
  if (factor > 1) {
    return fillTemplate(IMPORT_COPY.scaledSuffix, {
      factor: Math.round(factor).toLocaleString("tr-TR"),
    });
  }
  return fillTemplate(IMPORT_COPY.scaledSuffixDown, {
    factor: Math.round(1 / factor).toLocaleString("tr-TR"),
  });
}

// ---------------------------------------------------------------------------
// Sektör paketi öneri eşleşmesi (2026-10-01)
//
// DOSYA ADINA BAKAN KURAL YOK. Sektör iki kaynaktan gelir: (1) müşteri kaydının
// serbest metin `sector` alanı — ÖNCELİKLİ, danışmanın kendi yazdığı bilgidir;
// (2) kapak önerisinin `suggestion.sector` kodu. İkisi de boşsa öneri yapılmaz.
// ---------------------------------------------------------------------------
export interface SectorPackageLite {
  id: string;
  name: string;
  slug: string;
  /** Paket bu `SEK-*` bölümlerini hariç tutar — ikinci eşleşme kuralı buna dayanır. */
  excludedSectionCodes: readonly string[];
}

/** Satırın sektörü: müşteri kaydı önce, sonra kapak önerisi. */
export function resolveRowSectorCode(
  row: { suggestedSector: SectorCode | null },
  client: ClientOption | null | undefined,
): SectorCode | null {
  return normalizeSectorCode(client?.sector ?? null) ?? row.suggestedSector;
}

/**
 * P15/6 (parti cd8d §2) — MÜŞTERİ SEKTÖRÜ PAKETE DENK GELMEZSE KAPAĞA DÜŞ.
 *
 * Ozak gerçek vakası: `clients.sector = "İnşaat"`, oysa şirket GYO ve raporun kapağı
 * "Gayrimenkul Yatırım Ortaklığı" diyor. Müşteri kaydı ÖNCELİKLİ olduğu ve "İnşaat"
 * kodunun paketi olmadığı için parti satırında paket HİÇ seçilmedi → `SEK-KIMYA`
 * kapsama girdi, 6 gereksiz boş soru doğdu ve bölüm sayısı 11'e çıktı (+0,2 USD).
 *
 * Kural: müşteri sektörü denenir; paketi yoksa kapağın sektörü denenir. Hangi
 * kaynaktan geldiği dönüşte durur — ekran uyuşmazlık notunu ondan yazar.
 */
export interface SectorPackageMatch {
  packageId: string;
  /** Eşleşme hangi sektör kaynağından geldi. */
  source: "client" | "cover";
  clientCode: SectorCode | null;
  coverCode: SectorCode | null;
}

export function matchSectorPackage(
  row: { suggestedSector: SectorCode | null },
  client: ClientOption | null | undefined,
  packages: readonly SectorPackageLite[],
): SectorPackageMatch | null {
  const clientCode = normalizeSectorCode(client?.sector ?? null);
  const coverCode = row.suggestedSector;
  const byClient = clientCode === null ? null : packageForSector(clientCode, packages);
  if (byClient !== null) {
    return { packageId: byClient, source: "client", clientCode, coverCode };
  }
  const byCover = coverCode === null ? null : packageForSector(coverCode, packages);
  if (byCover !== null) {
    return { packageId: byCover, source: "cover", clientCode, coverCode };
  }
  return null;
}

/** Uyuşmazlık notu: paket KAPAĞA göre seçildi ve müşteri kaydı başka sektör diyor. */
export function sectorMismatchNote(match: SectorPackageMatch | null): string | null {
  if (match?.source !== "cover" || match.clientCode === null || match.coverCode === null) {
    return null;
  }
  if (match.clientCode === match.coverCode) return null;
  return fillTemplate(IMPORT_COPY.sectorMismatchNote, {
    client: SECTOR_LABELS[match.clientCode],
    cover: SECTOR_LABELS[match.coverCode],
  });
}

/**
 * Sektör kodundan paket seçer. İki kural:
 *   1. Paket adı == `SECTOR_LABELS[kod]` (yayınlarken türetilen paketler böyle adlanır).
 *   2. Paketin hariç tutmadığı tek `SEK-*` blok `SEK-<kod>` ise (elle açılmış,
 *      başka adlı paketler — tüm blok kümesi paketlerin exclusion birleşiminden türetilir).
 * Tek bir aday kalmazsa `null` döner: yanlış paket seçmek, seçmemekten kötüdür.
 */
export function suggestSectorPackage(
  row: { suggestedSector: SectorCode | null },
  client: ClientOption | null | undefined,
  packages: readonly SectorPackageLite[],
): string | null {
  return matchSectorPackage(row, client, packages)?.packageId ?? null;
}

/** Tek bir sektör kodunun paketi (iki kural); tek aday yoksa null. */
function packageForSector(code: SectorCode, packages: readonly SectorPackageLite[]): string | null {
  if (packages.length === 0) return null;

  const label = SECTOR_LABELS[code].trim().toLocaleLowerCase("tr");
  const byName = packages.filter((p) => p.name.trim().toLocaleLowerCase("tr") === label);
  if (byName.length === 1) return byName[0]!.id;

  // Tüm `SEK-*` blok kümesi: her paket ötekileri hariç tuttuğu için birleşim = tüm bloklar.
  const allBlocks = new Set<string>();
  for (const p of packages) {
    for (const c of p.excludedSectionCodes) if (isSectorBlockCode(c)) allBlocks.add(c);
  }
  if (allBlocks.size === 0) return null;
  const byRemaining = packages.filter((p) => {
    const excluded = new Set(p.excludedSectionCodes);
    const remaining = [...allBlocks].filter((c) => !excluded.has(c));
    return remaining.length === 1 && normalizeSectorCode(stripSectorPrefix(remaining[0]!)) === code;
  });
  return byRemaining.length === 1 ? byRemaining[0]!.id : null;
}

// ---------------------------------------------------------------------------
// Satır hazırlığı (framework-aware "zaten var")
// ---------------------------------------------------------------------------
export interface RowReadinessInput {
  clientId: string | null;
  frameworkId: string | null;
  reportingYear: number | null;
  periodType: PeriodType;
  hasFile: boolean;
}

export interface ExistingImportKey {
  clientId: string;
  frameworkId: string;
  reportingYear: number;
  periodType: PeriodType;
}

export type RowReadiness = "hazır" | "eksik" | "zaten var";

export function rowReadiness(
  row: RowReadinessInput,
  existing: readonly ExistingImportKey[],
): RowReadiness {
  if (!row.clientId || !row.frameworkId || !row.reportingYear || !row.hasFile) return "eksik";
  const dup = existing.some(
    (e) =>
      e.clientId === row.clientId &&
      e.frameworkId === row.frameworkId &&
      e.reportingYear === row.reportingYear &&
      e.periodType === row.periodType,
  );
  return dup ? "zaten var" : "hazır";
}

// ---------------------------------------------------------------------------
// Sonuç metinleri
// ---------------------------------------------------------------------------
/**
 * run3 K1 — YAZILAN YANIT (tek pay tanımı). `stats.answered` düşük güvenlileri DIŞLAR,
 * `form_responses`'a ise ikisi de yazılır: aynı aktarım için JobsTab "73/83", FormFill
 * bandı "77/83" diyordu (parti 2667 N1) ve beş raporun beşinde "doluluk geriledi" yanlış
 * alarmını üretiyordu. Pay her ekranda `answered + lowConfidence`; "k kontrol gerekli"
 * bu sayının ALT KÜMESİdir, yanına TOPLANMAZ.
 */
export function writtenAnswersFromStats(stats: ImportStats | null | undefined): number {
  if (!stats) return 0;
  return stats.answered + stats.lowConfidence;
}

/**
 * P11/4 — "x/y soru aktarıldı" içindeki y: yanıtlanan + boş + SORU düzeyinde reddedilen.
 * `answered + empty` yetmiyordu (düşük güvenli ve reddedilen yanıtlar toplamdan
 * düşüyordu). BÖLÜM ANLATISI reddi (`rejected.narrative`) soru DEĞİLDİR, toplama
 * girmez — eskiden `grounding`'e yazıldığı için toplamı şişiriyordu (inceleme).
 * Not: çıkarımı düşen bölümlerin soruları hiçbir sınıfa girmez (`failedSections`
 * onları ayrıca bildirir), bu yüzden toplam snapshot sayısının ALTINDA kalabilir.
 */
/**
 * run2-3 inceleme (4) — FormFill köken bandının PAYDASI. JobsTab ve ProjectDetail
 * `totalQuestionsFromStats` kullanıyor (dosya tipi soru D7'den beri çıkarım dışı, bu
 * yüzden hiçbir sayaca girmiyor) ama FormFill bandı snapshot'taki TÜM etkin soruları
 * sayıyordu → aynı aktarım için "81/84" ve "81/85" iki ayrı sayı görünüyordu.
 *
 * Köken yanıtı `stats` taşımadığı için (bkz. `ProjectProvenanceResponse`) payda burada
 * snapshot'tan hesaplanır ama AYNI kuralla: dosya tipi soru sayılmaz.
 */
export function importableQuestionTotal(
  questions: readonly { type: string; enabled?: boolean }[],
): number {
  return questions.filter((q) => q.enabled !== false && q.type !== "file").length;
}

export function totalQuestionsFromStats(stats: ImportStats | null | undefined): number {
  if (!stats) return 0;
  return (
    stats.answered +
    stats.empty +
    stats.lowConfidence +
    stats.rejected.grounding +
    stats.rejected.schema
  );
}

export interface ResultLines {
  primary: string;
  /** İstisna satırı — low/rejected/failed hepsi 0 ise null (5A: yalnız sıfır olmayanlar). */
  exceptions: string | null;
  /** Mono 3. satır — bölüm sayısı + eşlenmemiş + fallback notu. */
  details: string;
}

export function formatResultLines(stats: ImportStats, total: number): ResultLines {
  // run3 K1 — pay = yazılan yanıt (düşük güven DAHİL); bkz. `writtenAnswersFromStats`.
  const written = writtenAnswersFromStats(stats);
  const primary =
    written === 0
      ? IMPORT_COPY.resultEmpty
      : fillTemplate(IMPORT_COPY.resultPrimary, { answered: written, total });

  // 5A — istisna satırında YALNIZ sıfır olmayanlar görünür ("0 kontrol gerekli ·
  // 14 aktarılmadı · 0 bölüm işlenemedi" gürültüsü Task 13 manuel provasında
  // yakalandı). Etiketler tek kaynak: `IMPORT_COPY.resultExceptions` parçalanır.
  const rejectedTotal = stats.rejected.grounding + stats.rejected.schema;
  const failed = stats.failedSections.length;
  const exceptionSegments = IMPORT_COPY.resultExceptions.split(" · ");
  const exceptionParts = (
    [
      ["low", stats.lowConfidence],
      ["rejected", rejectedTotal],
      ["failed", failed],
    ] as const
  )
    .map(([key, value], i) => ({ key, value, template: exceptionSegments[i]! }))
    .filter((p) => p.value > 0)
    .map((p) => fillTemplate(p.template, { [p.key]: p.value }));
  // P6 — kısmi kabul yalnız > 0 iken istisna satırının sonuna eklenir.
  if (stats.partialAnswers > 0) {
    exceptionParts.push(fillTemplate(IMPORT_COPY.resultPartial, { partial: stats.partialAnswers }));
  }
  const exceptions = exceptionParts.length > 0 ? exceptionParts.join(" · ") : null;

  const detailParts = [
    fillTemplate(IMPORT_COPY.resultDetails, { sections: stats.sectionsWritten }),
  ];
  if (stats.unmappedSections.length > 0) {
    detailParts.push(
      fillTemplate(IMPORT_COPY.resultUnmapped, { n: stats.unmappedSections.length }),
    );
  }
  if (stats.fallbackToPdf) detailParts.push(IMPORT_COPY.fallbackNote);

  return { primary, exceptions, details: detailParts.join(" · ") };
}

// ---------------------------------------------------------------------------
// Dosya açma / rozet
// ---------------------------------------------------------------------------
/**
 * `ReportImport.files[]` → same-origin proxy URL'i (backend `fileProxyUrl` ikizi).
 * MinIO public değildir; `GET /api/v1/files/<storagePath>` auth'lu stream eder ve
 * `imports/` öneki yalnız `projects.import_report` izniyle okunur.
 */
export function importFileUrl(file: ReportImportFile): string {
  const encoded = file.storagePath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return `/api/v1/files/${encoded}?filename=${encodeURIComponent(file.fileName)}`;
}

/** Birincil dosya (K16: Word varsa o) — yoksa ilk dosya, hiç yoksa null. */
export function primaryImportFile(files: readonly ReportImportFile[]): ReportImportFile | null {
  return files.find((f) => f.primary) ?? files[0] ?? null;
}

export function fileOpenUrl(
  fileUrl: string,
  kind: "pdf" | "docx",
  pages?: readonly number[] | null,
): string {
  if (kind === "pdf" && pages && pages.length > 0) {
    return `${fileUrl}#page=${pages[0]!.toString()}`;
  }
  return fileUrl;
}

// ---------------------------------------------------------------------------
// Düzenleme sonrası tespiti (10A)
// ---------------------------------------------------------------------------
/** `updatedAt > finishedAt + 2s` → aktarımdan sonra elle düzenlendi (10A). */
export function isEditedAfterImport(responseUpdatedAt: string, importFinishedAt: string): boolean {
  return new Date(responseUpdatedAt).getTime() > new Date(importFinishedAt).getTime() + 2000;
}

// ---------------------------------------------------------------------------
// Eşzamanlılık sınırlı çalıştırıcı (7A) — presign/upload/suggest için.
// ---------------------------------------------------------------------------
export async function runWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length) as R[];
  let next = 0;
  async function worker(): Promise<void> {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i]!, i);
    }
  }
  const workerCount = Math.max(0, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

// ---------------------------------------------------------------------------
// Taslak (draft) — localStorage, kullanıcı bazlı (G5/G6). client-submission.ts
// (getLastClientSubmission) emsalinde: window guard + try/catch, sessiz başarısızlık.
// Şekil Task 13'e bırakılır (generic) — burada yalnız serialize/deserialize sözleşmesi.
// ---------------------------------------------------------------------------
const IMPORT_DRAFT_KEY_PREFIX = "mahir.import.draft.v1";

function draftStorageKey(userId: string): string {
  return `${IMPORT_DRAFT_KEY_PREFIX}.${userId}`;
}

export function readImportDraft<T = unknown>(userId: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(draftStorageKey(userId));
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeImportDraft<T>(userId: string, draft: T): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(draftStorageKey(userId), JSON.stringify(draft));
  } catch {
    // quota vb. — sessiz geç, taslak korunamasa da akış kesilmez
  }
}

export function clearImportDraft(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(draftStorageKey(userId));
  } catch {
    // erişilemiyorsa sessiz geç
  }
}

// ---------------------------------------------------------------------------
// FormFill filtre (Task 15 tüketir — burada saf + testli)
// ---------------------------------------------------------------------------
export type ImportQuestionFilter = "all" | "low" | "partial" | "rejected" | "empty";

export function filterQuestionCodes(
  codes: string[],
  provenance: ProjectProvenanceResponse | null,
  responsesByCode: Record<string, { value: unknown } | undefined>,
  filter: ImportQuestionFilter,
  resolvedRejections: Set<string> = new Set<string>(),
): string[] {
  if (filter === "all") return [...codes];

  const entriesByCode = new Map((provenance?.entries ?? []).map((e) => [e.questionCode, e]));

  if (filter === "empty") {
    // run3 K1 — ÇİP EVRENİ: "Cevaplanmamış" = evren − yazılan − (çözülmemiş) reddedilen.
    // Reddedilen sorunun değeri de boştur ama kendi çipinde sayılır; ikisinde birden
    // görünmesi aynı ekranda iki farklı toplam dolaştırıyordu (parti 2667 N6).
    return codes.filter(
      (code) =>
        !isFormValueFilled(responsesByCode[code]?.value) &&
        !(entriesByCode.get(code)?.rejected === true && !resolvedRejections.has(code)),
    );
  }

  if (!provenance) return [];

  if (filter === "low") {
    return codes.filter((code) => {
      const entry = entriesByCode.get(code);
      return !!entry && !entry.rejected && entry.confidence < provenance.threshold;
    });
  }

  // P6 (inceleme C3) — kısmi kabul AYRI kümedir: güveni yüksek olabilir, sorun satır
  // kaybıdır. "Düşük güven" çipinin altında sayılmaz.
  if (filter === "partial") {
    return codes.filter((code) => hasPartialRows(entriesByCode.get(code)));
  }

  // filter === "rejected"
  return codes.filter((code) => {
    const entry = entriesByCode.get(code);
    return !!entry && entry.rejected && !resolvedRejections.has(code);
  });
}

// ---------------------------------------------------------------------------
// Yükleme — presign → XHR PUT (ilerleme) → ReportImportFile (Eng review G2/RG5).
// ---------------------------------------------------------------------------
function fileKind(file: File): "pdf" | "docx" {
  return extOf(file.name) === "docx" ? "docx" : "pdf";
}

function mimeForKind(kind: "pdf" | "docx"): (typeof REPORT_IMPORT_ALLOWED_MIMES)[number] {
  // Pozisyonel [0]/[1] yerine içerik eşleşmesi — REPORT_IMPORT_ALLOWED_MIMES'in sırası
  // değişirse (ör. yeni bir mime eklenirse) sessizce yanlış tipe düşmesin (fix round 1).
  return kind === "pdf"
    ? REPORT_IMPORT_ALLOWED_MIMES.find((m) => m.includes("pdf"))!
    : REPORT_IMPORT_ALLOWED_MIMES.find((m) => !m.includes("pdf"))!;
}

/**
 * presign → uploadFileWithProgress → ReportImportFile kaydı. `primary` burada
 * `true` varsayılır (tek dosyalık akış) — Task 13 iki dosyayı (pdf+docx) tek
 * satırda birleştirirken K16 kuralına göre (docx varsa o birincil) günceller.
 * `pageCount` presign/upload adımında bilinmez (yalnız suggest endpoint'i sayfa
 * sayısı döner) — bilerek `undefined` bırakılır, bkz. task-12-report.md.
 */
export async function uploadImportFile(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<ReportImportFile> {
  const kind = fileKind(file);
  const mimeType = ALLOWED_MIME_SET.includes(file.type) ? file.type : mimeForKind(kind);
  const presign = await presignImportFile({
    fileName: file.name,
    mimeType: mimeType as (typeof REPORT_IMPORT_ALLOWED_MIMES)[number],
    sizeBytes: file.size,
  });
  try {
    await uploadFileWithProgress(presign.uploadUrl, file, onProgress);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw err instanceof Error ? err : new Error("Yükleme başarısız");
  }
  return {
    kind,
    storagePath: presign.storagePath,
    fileName: file.name,
    sizeBytes: file.size,
    primary: true,
  };
}

// ---------------------------------------------------------------------------
// Parti satırı modeli (Task 13) — "Yeni parti" sekmesinin saf mantığı.
// Bileşenler yalnız render eder; kararlar burada testlidir.
// ---------------------------------------------------------------------------

/** ✦ öneri / `touched` takibine giren alanlar (6A). */
export type ImportRowField =
  | "clientId"
  | "frameworkVersionId"
  | "sectorPackageId"
  | "reportingYear"
  | "periodType"
  | "reportType";

export type ImportRowUpload =
  | { phase: "idle" }
  | { phase: "uploading"; percent: number }
  | { phase: "suggesting" }
  | { phase: "failed" };

export interface ImportRowState {
  /** İstemci tarafı satır kimliği. */
  id: string;
  /** Gruplanmış dosyaların adları (birincil önce). */
  fileNames: string[];
  /** Yüklenmiş dosyalar — boşsa satır `eksik` (hasFile=false). */
  files: ReportImportFile[];
  pageCount: number | null;
  clientId: string | null;
  /**
   * Kapak önerisinin okuduğu şirket adı (eşleşen kayıt bulunmasa da dolar).
   * Satır içi "+ Yeni müşteri" editörünün ön-dolu değeri ve aynı adın öteki
   * satırlara yayılması bu alandan okunur.
   */
  suggestedClientName: string | null;
  /** Kapak önerisinin sektör kodu (müşteri kaydında sektör yoksa kullanılır). */
  suggestedSector: SectorCode | null;
  frameworkVersionId: string | null;
  /** `frameworkVersionId`'den türetilir — "zaten var" kontrolü framework bazlıdır. */
  frameworkId: string | null;
  sectorPackageId: string | null;
  reportingYear: number | null;
  periodType: PeriodType;
  /** Sabit liste id'si (`REPORT_TYPES`); parti gövdesine ETİKET olarak yazılır. */
  reportType: ReportType | null;
  /** Kullanıcının elle dokunduğu alanlar — öneri bir daha ÜZERİNE YAZMAZ (6A). */
  touched: ImportRowField[];
  /** ✦ işareti gösterilecek alanlar (öneriden geldi, elle değiştirilmedi). */
  suggested: ImportRowField[];
  upload: ImportRowUpload;
  /** 409 `duplicate_active` detayı ya da istemci tarafı tespit. */
  duplicateReason: "active_import" | "existing_project" | null;
  /** "Mevcut projeyi aç" linki için (6A). */
  existingProjectId: string | null;
}

/**
 * ✦ öneriyi satıra uygular. `touched` içindeki alan ASLA ezilmez (6A); null öneri
 * alanları da yazılmaz. Elle düzeltme `markRowTouched` ile ✦ işaretini kaldırır.
 */
export function applySuggestionToRow(
  row: ImportRowState,
  suggestion: ImportSuggestion,
): ImportRowState {
  const next: ImportRowState = { ...row };
  const suggested = new Set(row.suggested);
  const touched = new Set(row.touched);

  function put(field: ImportRowField, apply: () => void): void {
    if (touched.has(field)) return;
    apply();
    suggested.add(field);
  }

  // Ad, eşleşen kayıt BULUNMASA da saklanır — "+ Yeni müşteri" editörünün ön-dolu
  // değeri ve satırlar arası yayılma anahtarı budur (`touched` damgası aramaz;
  // kullanıcının seçimini değiştirmez, yalnız kapakta okunanı taşır).
  if (suggestion.clientName !== null && suggestion.clientName !== "") {
    next.suggestedClientName = suggestion.clientName;
  }
  // Sektör de `touched` damgası aramaz: bir ALAN değil, paket önerisinin girdisidir
  // (kullanıcı paketi elle seçtiyse `touched` zaten paket önerisini durdurur).
  if (suggestion.sector !== null) next.suggestedSector = suggestion.sector;
  if (suggestion.suggestedClientId !== null) {
    put("clientId", () => {
      next.clientId = suggestion.suggestedClientId;
    });
  }
  if (suggestion.year !== null) {
    put("reportingYear", () => {
      next.reportingYear = suggestion.year;
    });
  }
  if (suggestion.periodType !== null) {
    put("periodType", () => {
      next.periodType = suggestion.periodType!;
    });
  }
  // Ham etiket DEĞİL, normalize edilmiş id yazılır — kapak başlığı ("YÖNETİM KURULU
  // FAALİYET RAPORU") alana olduğu gibi düşmez; eşleşme yoksa alan boş + ✦ yok.
  if (suggestion.reportTypeId !== null) {
    put("reportType", () => {
      next.reportType = suggestion.reportTypeId;
    });
  }
  if (suggestion.pageCount !== null) next.pageCount = suggestion.pageCount;
  next.suggested = [...suggested];
  return next;
}

/**
 * Elle düzenleme — alan `touched` olur, ✦ kalkar (6A). Sunucudan gelen 409
 * `duplicate_active` damgası da SIFIRLANIR: kullanıcı kimliği değiştirdiyse
 * (müşteri/yıl/dönem/çerçeve) artık aynı çakışma geçerli değildir ve satır
 * yeniden denenebilir olmalıdır (fix round 1).
 */
export function markRowTouched(row: ImportRowState, field: ImportRowField): ImportRowState {
  return {
    ...row,
    touched: row.touched.includes(field) ? row.touched : [...row.touched, field],
    suggested: row.suggested.filter((f) => f !== field),
    duplicateReason: null,
  };
}

/**
 * Satır durumu. `duplicateReason` (sunucunun 409 `duplicate_active` yanıtı)
 * istemci tarafı ön kontrolünü EZER — sunucu son sözü söyler; eksik alan varsa
 * `eksik` önceliklidir (kullanıcı önce onu doldurmalı).
 */
export function rowReadinessOf(
  row: ImportRowState,
  existing: readonly ExistingImportKey[],
): RowReadiness {
  const base = rowReadiness(
    {
      clientId: row.clientId,
      frameworkId: row.frameworkId,
      reportingYear: row.reportingYear,
      periodType: row.periodType,
      hasFile: row.files.length > 0,
    },
    existing,
  );
  if (base === "eksik") return base;
  return row.duplicateReason !== null ? "zaten var" : base;
}

export interface BatchReduction {
  /** `POST /batches` gövdesine giren satırlar — YALNIZ `hazır` olanlar (6A kısmi işleme). */
  rows: ImportBatchRow[];
  /** `rows` ile aynı sıradaki satır id'leri — 409 `rowIndex` eşlemesi için. */
  rowIds: string[];
  /** İşlenmeyecek satır sayısı (`eksik` + `zaten var`) — `skippedRowsNote`. */
  skipped: number;
}

/** Hazır satırları parti gövdesine indirger; gerisini sayar (6A). */
export function reduceRowsForBatch(
  rows: readonly ImportRowState[],
  existing: readonly ExistingImportKey[],
): BatchReduction {
  const out: BatchReduction = { rows: [], rowIds: [], skipped: 0 };
  for (const row of rows) {
    if (rowReadinessOf(row, existing) !== "hazır") {
      out.skipped += 1;
      continue;
    }
    // DB/DTO şekli korunur: `report_imports.report_type` serbest metin, ETİKET yazılır.
    const label = row.reportType ? REPORT_TYPE_LABELS[row.reportType] : "";
    out.rows.push({
      files: row.files,
      clientId: row.clientId!,
      frameworkVersionId: row.frameworkVersionId!,
      ...(row.sectorPackageId ? { sectorPackageId: row.sectorPackageId } : {}),
      reportingYear: row.reportingYear!,
      periodType: row.periodType,
      ...(label ? { reportType: label } : {}),
    });
    out.rowIds.push(row.id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Satır içi "+ Yeni müşteri" (saf) — benzer kayıt sıralaması + aynı adın yayılması.
// ---------------------------------------------------------------------------

export interface ClientOption {
  id: string;
  name: string;
  /** `clients.sector` serbest metni — paket önerisinde `normalizeSectorCode`'dan geçer. */
  sector: string | null;
}

/**
 * Yeni müşteri adına en benzeyen MEVCUT kayıtlar (en çok `limit` tane).
 * Ölçü shared `companyNameSimilarity`; eşik `COMPANY_NAME_SIMILAR_MIN`'in altındaki
 * kayıt hiç listelenmez (uyarı satırı boşsa çağıran hiç çizmez). Eşitlikte ad sırası
 * — aynı skorlu iki kayıt arasında rastgele sıra kullanıcıya kararsız görünürdü.
 */
export function similarClientOptions(
  name: string,
  clients: readonly ClientOption[],
  limit = 2,
): ClientOption[] {
  const scored = clients
    .map((c) => ({ client: c, score: companyNameSimilarity(name, c.name) }))
    .filter((s) => s.score >= COMPANY_NAME_SIMILAR_MIN)
    .sort((a, b) => b.score - a.score || a.client.name.localeCompare(b.client.name, "tr"));
  return scored.slice(0, Math.max(0, limit)).map((s) => s.client);
}

/**
 * Yazılan adın normalize halde AYNI olduğu mevcut kayıt (varsa) — `Oluştur` bunun
 * üzerine ikinci bir kayıt açmaz (inceleme: mükerrer müşteri kaydı önceki-yıl
 * önerisini de bozuyor; API ad tekilliği uygulamıyor, kapı burada).
 *
 * run2 D3: ölçü `companyNameSimilarity === 1` DEĞİL, normalize EŞİTLİK. O fonksiyon
 * ALT DİZE kapsamasını da 1 sayıyor ("Torunlar" ⊂ "Torunlar GYO") — kısa adlı bir
 * kayıt varken o markayı taşıyan hiçbir yeni tüzel kişi açılamıyordu ("X Holding"
 * varken "X Holding Enerji"). Alt dize artık yalnız "Benzer kayıtlar" satırına düşer.
 */
export function exactClientMatch(
  name: string,
  clients: readonly ClientOption[],
): ClientOption | null {
  const normalized = normalizeCompanyName(name);
  if (normalized.length === 0) return null;
  return clients.find((c) => normalizeCompanyName(c.name) === normalized) ?? null;
}

/**
 * Kapak/yazılan şirket adından sunucu araması için kısa terim (ClientPicker işi, 2026-10-07):
 * normalize adın İLK anlamlı (≥3 karakter) parçası — "Sinpaş GYO A.Ş." → "sinpas". Tam ad
 * `%LIKE%` ile aranırsa benzer-ama-alt-dize-olmayan kayıt ("SİNPAŞ Gayrimenkul") hiç
 * gelmez; marka parçası aday havuzunu geniş tutar, sıralamayı `similarClientOptions` yapar.
 * Parça yoksa normalize ad, o da boşsa ham ad (kırpılmış) döner.
 */
export function clientSearchTerm(name: string | null | undefined): string {
  const raw = (name ?? "").trim();
  if (raw.length === 0) return "";
  const normalized = normalizeCompanyName(raw);
  const token = normalized.split(/\s+/).find((t) => t.length >= 3);
  return token ?? (normalized.length > 0 ? normalized : raw);
}

/**
 * Bir müşteri kaydı yaratıldıktan sonra AYNI şirketin öteki satırlarını da seçer —
 * bir firmanın iki dosyası partide iki kayıt açmasın (brief §1).
 *
 * Yalnız: müşterisi hâlâ boş + kullanıcı o alana elle dokunmamış + kapak önerisinin
 * şirket adı normalize halde aynı olan satırlar. Yazılan alan ✦ (öneri) sayılır:
 * seçim kapaktan türedi, kullanıcının elinden çıkmadı.
 */
export function propagateClientSelection(
  rows: readonly ImportRowState[],
  suggestedName: string,
  clientId: string,
): ImportRowState[] {
  const key = normalizeCompanyName(suggestedName);
  if (key.length === 0) return [...rows];
  return rows.map((row) => {
    if (row.clientId !== null || row.touched.includes("clientId")) return row;
    if (normalizeCompanyName(row.suggestedClientName ?? "") !== key) return row;
    return {
      ...row,
      clientId,
      suggested: row.suggested.includes("clientId")
        ? row.suggested
        : [...row.suggested, "clientId"],
    };
  });
}

/** SSE `event: report_import` → liste birleştirme: varsa yerinde güncelle, yoksa başa ekle. */
export function upsertImportRow(list: readonly ReportImport[], row: ReportImport): ReportImport[] {
  const idx = list.findIndex((r) => r.id === row.id);
  if (idx === -1) return [row, ...list];
  const next = [...list];
  next[idx] = row;
  return next;
}

/** Parti kısa kimliği — `#a1b2` (İşler tablosu "Parti" kolonu). */
export function shortBatchId(batchId: string): string {
  return `#${batchId.replace(/-/g, "").slice(0, 4)}`;
}

/**
 * Taslağa yazılacak / taslaktan okunacak satırları normalleştirir (7A, fix round 1).
 *
 * Ham `File` nesneleri serileştirilemez ve sayfa yenilenince kaybolur; bu yüzden:
 *   · yüklemesi tamamlanmamış satırlar (`files` boş) taslağa GİRMEZ — geri
 *     yüklenseler "Yeniden dene" düğmesi ölü olurdu (elde File yok),
 *   · kalan satırların `upload` fazı `idle`'a çekilir (bayat `uploading %62`
 *     ya da `Öneri hesaplanıyor…` göstermemek için),
 *   · sunucu/önbellek türevi alanlar (`duplicateReason`, `existingProjectId`)
 *     sıfırlanır — dönüşte yeniden hesaplanır.
 */
export function normalizeDraftRows(rows: readonly ImportRowState[]): ImportRowState[] {
  return rows
    .filter((r) => r.files.length > 0)
    .map((r) => ({
      ...r,
      // Eski taslaklarda alan hiç yoktu — okumada da normalleştirilir.
      suggestedClientName: r.suggestedClientName ?? null,
      // Eski taslaklarda alan yok; sözlük dışı/bozuk değer null'a düşer.
      suggestedSector: normalizeSectorCode(r.suggestedSector ?? null),
      // Serbest metin dönemindeki taslaklar ("Faaliyet Raporu") sabit liste id'sine
      // indirilir; karşılığı olmayan değer düşer (alan boş kalır).
      reportType: coerceReportType(r.reportType),
      upload: { phase: "idle" },
      duplicateReason: null,
      existingProjectId: null,
    }));
}

// ---------------------------------------------------------------------------
// Task 14 — ProjectDetail "Rapordan doldur" + `ImportSummarySection`.
// `provenance.imports[]` yalnız `ImportSummary` taşır (id/mode/status/fileName/
// fileUrl/fileKind/finishedAt) — sayaçlar (`stats`/`unmappedCount`) taşımaz;
// bunlar için çağıran `getImport(latestImportId)` ile ayrıca detay çeker (T1
// `ImportSummarySchema`, api-route-table.md).
// ---------------------------------------------------------------------------

/** Aktif sayılan durumlar — "Rapordan doldur" düğmesi bunlar sürerken kapanır (3A). */
const FILL_ACTIVE_STATUSES: ReadonlySet<ReportImportStatus> = new Set([
  "uploaded",
  "queued",
  "mapping",
  "extracting",
  "writing",
]);

/** Bir projede sürmekte olan (henüz `done`/terminal olmamış) import var mı? */
export function hasActiveImport(imports: readonly ImportSummary[]): boolean {
  return imports.some((i) => FILL_ACTIVE_STATUSES.has(i.status));
}

/**
 * `provenance.imports[]` içinden en son import'u seçer — `ImportSummary` `createdAt`
 * taşımadığı için `finishedAt`'a göre sıralanır; hâlâ süren (finishedAt=null) kayıt
 * "en yeni" sayılır (kullanıcı az önce başlattığı için ekranda önde olmalı). create ve
 * fill eşit muamele görür — ikinci (veya üçüncü) fill de "en son" olabilir.
 */
export function pickLatestImport(imports: readonly ImportSummary[]): ImportSummary | null {
  if (imports.length === 0) return null;
  const [first, ...rest] = imports;
  return rest.reduce((latest, cur) => {
    const latestTime = latest.finishedAt ? Date.parse(latest.finishedAt) : Number.POSITIVE_INFINITY;
    const curTime = cur.finishedAt ? Date.parse(cur.finishedAt) : Number.POSITIVE_INFINITY;
    return curTime >= latestTime ? cur : latest;
  }, first!);
}

// ---------------------------------------------------------------------------
// Task 15 — FormFill köken yardımcıları (2A / 11A / 13A). Saf; bileşenler yalnız
// render eder, karar buradadır.
// ---------------------------------------------------------------------------

/**
 * 2A — SectionNav'daki "aktif çipe göre bölüm başına eşleşen soru" rozeti.
 * Anahtar kümesi korunur (eşleşmeyen bölüm 0 döner) — rozet "0" yazabilsin ve
 * bölüm listesi filtreye göre kaybolmasın.
 */
export function sectionCountsForFilter(
  questionsBySection: Readonly<Record<string, readonly string[]>>,
  filteredCodes: readonly string[],
): Record<string, number> {
  const matched = new Set(filteredCodes);
  const out: Record<string, number> = {};
  for (const [sectionId, codes] of Object.entries(questionsBySection)) {
    out[sectionId] = codes.reduce((n, c) => (matched.has(c) ? n + 1 : n), 0);
  }
  return out;
}

/**
 * 14A — rozet/satır metnindeki kaynak işaretçisi. PDF'te sayfa (`s.136`), DOCX'te
 * kısa locator (`§3.1 · p.4`). `aria` tam etiketin `{page}` yuvasına girer.
 * Hiç işaretçi yoksa `null` → rozet çizilmez (rozetin TEK işi kaynağı göstermek).
 */
export interface ProvenanceRef {
  /** `kind` aria şablonunu seçer: PDF'te "sayfa {page}", DOCX'te düz "{locator}". */
  kind: "page" | "locator";
  label: string;
  aria: string;
}

export function provenanceRefLabel(
  entry: ResponseProvenance | null | undefined,
): ProvenanceRef | null {
  if (!entry) return null;
  const page = entry.sourcePages?.[0];
  if (page !== undefined) {
    return {
      kind: "page",
      label: fillTemplate(IMPORT_COPY.badgePage, { page }),
      aria: page.toString(),
    };
  }
  if (entry.sourceLocator) {
    return {
      kind: "locator",
      label: fillTemplate(IMPORT_COPY.badgeLocator, { locator: entry.sourceLocator }),
      aria: entry.sourceLocator,
    };
  }
  return null;
}

/**
 * R15 — "bu değeri kullan" düğmesi yalnız GERÇEKTEN yazılabilir öneride çıkar.
 *
 * `schema` reddinde backend ham model çıktısını saklar
 * (`services/report-import/writer.ts` — `proposedValue = invalid.raw`); bu değer
 * tanım gereği soru tipine UYMAZ, yazılırsa doğrulama hatası üretir. O yüzden
 * schema reddinde öneri salt-okunur gösterilir (alıntı + sebep).
 * `grounding` reddinde öneri şema-geçerlidir (`draft.rejected.proposedValue`) —
 * yazılabilir. `proposedValue === null` ise yazacak bir şey yoktur.
 */
export function canUseProposal(entry: ResponseProvenance | null | undefined): boolean {
  if (!entry?.rejected) return false;
  if (entry.rejectedReason !== "grounding") return false;
  return entry.proposedValue !== null;
}

/**
 * run2 D9 — SAKLANAN öneri şeklini FORM değeri şekline çevirir.
 *
 * Seçim tipinde backend `proposedValue` olarak modelin `optionIds` DİZİSİNİ saklıyor
 * (`verify-answer.ts verifyChoiceAnswer`), form değeri ise `{optionId}` / `{optionIds}`.
 * Çevirmeden: (a) `formatFormValue` şekli tanımaz, popover ham JSON + ham seçenek
 * kimliği gösterirdi (`["opt_hayir"]` — kullanıcı "Hayır" göremiyordu), (b) "bu değeri
 * kullan" soru tipine uymayan bir değer yazardı. İki yol da bu TEK çeviriden geçer.
 *
 * Tanınmayan şekil AYNEN döner — uydurma yok, çağıran mevcut fallback'ine düşer.
 */
export function proposalFormValue(value: unknown, question: { type: string }): unknown {
  if (!Array.isArray(value)) return value;
  const ids = value.filter((v): v is string => typeof v === "string");
  if (ids.length !== value.length || ids.length === 0) return value;
  if (question.type === "single") return ids.length === 1 ? { optionId: ids[0] } : value;
  if (question.type === "multi") return { optionIds: ids };
  return value;
}

/** 13A — soru kartında hangi köken satırının çizileceği. `null` → köken UI'sı yok. */
export type ProvenanceLineKind = "badge" | "low" | "partial" | "rejected" | "accepted-manual";

/** P6 — popover'da en fazla bu kadar reddedilen satır listelenir (kalanı sayıyla söylenir). */
export const PARTIAL_ROWS_VISIBLE = 8;

/** P6 — yanıt yazıldı ama en az bir satır/parça doğrulanamadı mı? */
export function hasPartialRows(entry: ResponseProvenance | null | undefined): boolean {
  return !!entry && !entry.rejected && (entry.partial?.rejectedRows.length ?? 0) > 0;
}

/**
 * 13A tek fiil dili + 11A öneri kabulü türetimi:
 *   kabul edilmiş ∧ confidence ≥ threshold → `badge`  (nötr `s.N` pill)
 *   kabul edilmiş ∧ confidence <  threshold → `low`    (sol amber bar, "Kontrol gerekli")
 *   reddedilmiş  ∧ (resolved ∨ yanıt dolu) → `accepted-manual` (nötr "Öneri elle kabul edildi")
 *   reddedilmiş  ∧ aksi                    → `rejected` (sol kırmızı bar + "öneriyi gör")
 *
 * `resolved` yalnız oturum içi açık kabul ("bu değeri kullan") bayrağıdır; kalıcı
 * kapanış `isFormValueFilled(value)` türetiminden gelir (11A — sunucu kaydı aynen kalır).
 */
export function provenanceLineKind(
  entry: ResponseProvenance | null | undefined,
  threshold: number,
  value: unknown,
  resolved: boolean,
): ProvenanceLineKind | null {
  if (!entry) return null;
  if (entry.rejected) {
    return resolved || isFormValueFilled(value) ? "accepted-manual" : "rejected";
  }
  // P6 öncelik sırası: rejected > low > partial > badge. `low` ∧ `partial` durumunda
  // `low` döner ve ProvenanceLine kısmi satırı ALTINA ekler (tasarım kararı) —
  // "Kontrol gerekli" uyarısı kısmi kabul yüzünden kaybolmaz.
  if (entry.confidence < threshold) return "low";
  return hasPartialRows(entry) ? "partial" : "badge";
}

/**
 * FormFill bandı projeyi YARATAN aktarımı gösterir (sonraki "Rapordan doldur"
 * çalışmaları bandı değiştirmez — `pickLatestImport` ProjectDetail'in işidir).
 * Devam eden (finishedAt null) create satırı en yeni sayılır.
 */
export function latestCreateImport(imports: readonly ImportSummary[]): ImportSummary | null {
  const creates = imports.filter((i) => i.mode === "create");
  if (creates.length === 0) return null;
  return pickLatestImport(creates);
}

/**
 * Fill akışında yüklenmiş dosyaları `FillProjectFromReportRequest.files`'a hazırlar
 * (16A/eng review — aynı gövde kuralı): tek dosya → birincil; iki dosya → gövdeleri
 * eşleşmeli VE farklı türde olmalı (FilesSchema kuralı), eşleşirse Word birincil olur
 * (K16). Eşleşmezse ikinci dosya reddedilir (`{ error: "pair" }`, çağıran `pairNote`
 * gösterir) — `uploadImportFile` her zaman `primary:true` döndüğü için burada üzerine
 * yazılır.
 */
export function pairFilesForFill(
  files: readonly ReportImportFile[],
): { files: ReportImportFile[] } | { error: "pair" } {
  if (files.length === 0 || files.length > 2) return { error: "pair" };
  if (files.length === 1) return { files: [{ ...files[0]!, primary: true }] };
  const [a, b] = files as [ReportImportFile, ReportImportFile];
  if (stemOf(a.fileName) !== stemOf(b.fileName)) return { error: "pair" };
  if (a.kind === b.kind) return { error: "pair" };
  return { files: [a, b].map((f) => ({ ...f, primary: f.kind === "docx" })) };
}
